import React, { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import css from './EngagementStats.module.css'
// Rank uses the member list's number style so both lists line up
import itemCss from '../../atoms/UserListItem/UserListItem.module.css'
import SegmentedInput from '../../inputs/SegmentedInput/SegmentedInput'
import Avatar from '../../atoms/Avatar/Avatar'
import { CheckCircle, Eye, Icon, SignIn } from '@phosphor-icons/react'
import { MemberName, MemberStats } from '../../atoms/UserListItem/UserListItem'
import LoadingSkeleton from '../../utility/LoadingSceleton/LoadingSkeleton'
import { GymEngagement, GymMemberData } from '../../../types/types'
import { joinedAtMs, trackedAtMs } from '../../../utilities/memberDates'

type Period = 'last-30-days' | 'billing-period'

type Props = {
  members: GymMemberData[]
  engagement: GymEngagement | undefined
  isLoading: boolean
}

const TOP_COUNT = 10

/**
 * Chicago Manual of Style date range: months spelled out, closed en dash,
 * shared month/year not repeated. E.g. "September 2–October 2, 2026"
 */
const formatDateRange = (startMs: number, endMs: number) => {
  const start = DateTime.fromMillis(startMs)
  const end = DateTime.fromMillis(endMs)

  if (start.year !== end.year)
    return `${start.toFormat('LLLL d, yyyy')}–${end.toFormat('LLLL d, yyyy')}`
  if (start.month !== end.month)
    return `${start.toFormat('LLLL d')}–${end.toFormat('LLLL d, yyyy')}`
  if (start.day !== end.day)
    return `${start.toFormat('LLLL d')}–${end.toFormat('d, yyyy')}`
  return end.toFormat('LLLL d, yyyy')
}

const EngagementStats = ({ members, engagement, isLoading }: Props) => {
  const [period, setPeriod] = useState<Period>('last-30-days')

  const billingPeriod = engagement?.billingPeriod
  const lastOpenedAt = engagement?.lastOpenedAt
  const workoutCounts = engagement?.workoutCounts
  const isBillingPeriod = period === 'billing-period' && !!billingPeriod

  // Period starts come from the server so labels and counts use the same
  // boundaries. Until it responds, compute "last 30 days" the same way:
  // today and the 29 days before it, from midnight. Counts run until now.
  const periodStart =
    isBillingPeriod && billingPeriod
      ? billingPeriod.start
      : engagement?.last30Days.start ??
        DateTime.now().startOf('day').minus({ days: 29 }).toMillis()

  const workoutsOf = (member: GymMemberData) => {
    const counts = workoutCounts?.[member.uid]
    return (isBillingPeriod ? counts?.billingPeriod : counts?.last30Days) ?? 0
  }

  const publishedCount = isBillingPeriod
    ? engagement?.publishedWorkouts?.billingPeriod
    : engagement?.publishedWorkouts?.last30Days

  /** Share of the period's published workouts they tracked, or null */
  const percentOfPublished = (workouts: number) =>
    publishedCount
      ? Math.min(100, Math.round((workouts / publishedCount) * 100))
      : null

  // Same record billing uses for active members (set on every tracked result)
  const hasTracked = (member: GymMemberData) =>
    trackedAtMs(member) >= periodStart

  const trackedCount = members.filter(hasTracked).length

  // Tracking a workout means they viewed it, so "viewed" includes "tracked"
  const viewedCount = lastOpenedAt
    ? members.filter(
        (m) => hasTracked(m) || (lastOpenedAt[m.uid] ?? 0) >= periodStart
      ).length
    : null

  // Current members only: people who joined and left aren't on this list
  const joinedCount = members.filter((m) => joinedAtMs(m) >= periodStart).length

  const topMembers = members
    .map((member) => ({
      member,
      workouts: workoutsOf(member),
    }))
    .filter(({ workouts }) => workouts > 0)
    .sort(
      (a, b) =>
        b.workouts - a.workouts ||
        (a.member.name || '').localeCompare(b.member.name || '')
    )
    .slice(0, TOP_COUNT)

  const unavailableHint = 'Recent activity is unavailable right now'

  return (
    // Not a <section>: #page-content section gets a white card in App.css
    <div
      className={css['engagement']}
      role="region"
      aria-label="Recent activity"
    >
      <header className={css['header']}>
        <h3 style={{ margin: 0 }}>
          Recent activity
          <FadeText
            className={css['range']}
            // Billing shows the whole period (it runs past today); counts
            // still cover its start until now
            text={formatDateRange(
              periodStart,
              isBillingPeriod && billingPeriod ? billingPeriod.end : Date.now()
            )}
          />
        </h3>
        <SegmentedInput
          segments={[
            { label: 'Last 30 days', value: 'last-30-days' },
            ...(billingPeriod
              ? [{ label: 'Billing period', value: 'billing-period' }]
              : []),
          ]}
          value={period}
          onChange={(newValue) => setPeriod(newValue as Period)}
          style={{ width: billingPeriod ? '240px' : '120px' }}
        />
      </header>

      <section className={css['kpis']}>
        <Kpi
          label="Viewed workouts"
          icon={Eye}
          value={viewedCount}
          isLoading={isLoading}
          hint={
            !isLoading && viewedCount === null
              ? unavailableHint
              : 'Opened your gym’s workouts in the mobile app, or tracked a workout'
          }
        />
        <Kpi
          label="Tracked a workout"
          icon={CheckCircle}
          value={trackedCount}
          hint="Logged at least one result at your gym"
        />
        <Kpi
          label="New members"
          icon={SignIn}
          value={joinedCount}
          hint="Joined your gym in this period and still have access"
        />
      </section>

      <div className={css['top-heading']}>
        <h3 className={css['subheading']} style={{ margin: 0 }}>
          Top members
          <span className={css['range']}>by workouts tracked</span>
        </h3>
      </div>
      <section>
        {isLoading ? (
          <TopMembersSkeleton />
        ) : !workoutCounts ? (
          <p className={css['empty']}>{unavailableHint}</p>
        ) : !topMembers.length ? (
          <p className={css['empty']}>No workouts tracked in this period yet</p>
        ) : (
          <ol className={css['top']}>
            {topMembers.map(({ member, workouts }, index) => (
              <li key={member.uid} className={css['top-item']}>
                <span className={itemCss['index']}>{index + 1}</span>
                <Avatar
                  name={member.name}
                  photoURL={member.photoURL}
                  size={56}
                  zoomable
                />
                <div className={css['top-text']}>
                  <MemberName data={member} />
                  <div className={itemCss['secondary']}>
                    <MemberStats
                      data={member}
                      // Same as the member list: no view dates if PostHog failed
                      lastOpenedAt={
                        lastOpenedAt
                          ? lastOpenedAt[member.uid] ?? null
                          : undefined
                      }
                    />
                  </div>
                </div>
                <div className={css['top-score']}>
                  <span className={css['top-count']}>{workouts}</span>
                  {percentOfPublished(workouts) !== null && (
                    <span
                      className={css['top-percent']}
                      title={`Tracked ${workouts} of ${publishedCount} published workouts`}
                    >
                      {percentOfPublished(workouts)}%
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  )
}

const FADE_MS = 150

/** Fades out the old text and fades in the new one when `text` changes */
const FadeText = ({
  text,
  className,
}: {
  text: string
  className?: string
}) => {
  const [shownText, setShownText] = useState(text)
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    // Also covers switching back before the fade-out finished
    if (text === shownText) {
      setIsVisible(true)
      return
    }
    setIsVisible(false)
    const timeout = setTimeout(() => {
      setShownText(text)
      setIsVisible(true)
    }, FADE_MS)
    return () => clearTimeout(timeout)
  }, [text, shownText])

  return (
    <span
      className={`${className} ${css['fade']} ${
        isVisible ? '' : css['hidden']
      }`}
      aria-live="polite"
    >
      {shownText}
    </span>
  )
}

type KpiProps = {
  label: string
  /** Same icon as the matching date in member rows */
  icon: Icon
  value: number | null
  hint: string
  isLoading?: boolean
}

const Kpi = ({ label, icon: KpiIcon, value, hint, isLoading }: KpiProps) => (
  <div className={css['kpi']} title={hint} aria-busy={isLoading}>
    <div className={css['kpi-value']}>
      {isLoading ? (
        <LoadingSkeleton
          type="block"
          style={{ width: '64px', height: '44px', borderRadius: '6px' }}
        />
      ) : value === null ? (
        '—'
      ) : (
        value
      )}
    </div>
    <div className={css['kpi-label']}>
      <KpiIcon
        size={16}
        weight="bold"
        aria-hidden
        className={css['kpi-icon']}
      />
      {label}
    </div>
  </div>
)

// One per top member (TOP_COUNT), fixed so the skeleton doesn't jump
const SKELETON_NAME_WIDTHS = [168, 132, 196, 148, 120, 176, 140, 160, 124, 184]

const TopMembersSkeleton = () => (
  <ol className={css['top']} aria-busy="true" aria-label="Loading top members">
    {SKELETON_NAME_WIDTHS.map((width, index) => (
      <li key={index} className={css['top-item']}>
        <span className={itemCss['index']}>{index + 1}</span>
        <LoadingSkeleton
          type="block"
          style={{ width: '56px', height: '56px', borderRadius: '50%' }}
        />
        {/* Bars centered in the name (24px) and stats (20px) lines */}
        <span className={css['top-text']}>
          <LoadingSkeleton
            type="block"
            style={{
              width: `${width}px`,
              height: '16px',
              margin: '4px 0',
              borderRadius: '4px',
            }}
          />
          <LoadingSkeleton
            type="block"
            style={{
              width: '220px',
              height: '12px',
              margin: '8px 0 4px',
              borderRadius: '4px',
            }}
          />
        </span>
        <LoadingSkeleton
          type="block"
          style={{ width: '32px', height: '20px', borderRadius: '4px' }}
        />
      </li>
    ))}
  </ol>
)

export default EngagementStats
